import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef
} from 'react'
import { Grid } from '../../components/grid/Grid'
import { Puzzle } from '../../components/puzzle/Puzzle'
import { Keyboard } from '../../components/keyboard/Keyboard'
import { Ad } from '../../components/ads/Ad'
import { useAlert } from '../../context/AlertContext'
import { Transition } from '@headlessui/react'
import {
  NOT_ENOUGH_LETTERS_MESSAGE,
  WORD_NOT_FOUND_MESSAGE,
  PATTERN_MUST_MATCH_MESSAGE,
  CORRECT_WORD_MESSAGE,
  GA_CATEGORY_GAME,
  GA_ACTION_GUESS,
  GA_ACTION_GAME_FINISH,
  ALERT_DATA_GAME_END,
  ALERT_DATA_GUESS,
  ARROW_LEFT,
  ARROW_RIGHT,
  CURSOR_TYPE_MULTI,
  GameMode,
} from '../../constants/strings'
import {
  MAX_CHALLENGES,
} from '../../constants/settings'
import {
  findFirstUnusedReveal,
  isSamePattern,
  unicodeLength,
  Solution,
  WordList,
  isOrdinary
} from '../../lib/words'
import { getAchievements } from '../../lib/achievements'
import { User } from '../../lib/user'
import { Settings } from '../../lib/settings'
import { cursorDelete } from '../../lib/cursor'
import { addStatsForCompletedGame, grantAchievement, setStatsLocalAndRemote } from '../../lib/stats'
import { getRevealTime } from '../../lib/time'
import {
  StoredGameState,
  GameStats,
  getStoredGameState,
  setStoredGameState,
} from '../../lib/localStorage'
import { default as GraphemeSplitter } from 'grapheme-splitter'
import ReactGA from 'react-ga4'
import { useMediaQuery } from 'react-responsive'
import { PostGameInfo } from './PostGameInfo'
import { useAchievements } from '../../context/AchievementsContext'

export type Actions = {
  handleShareToClipboard: () => void
  handleShareFailure: () => void
  onNextPuzzle: () => void
  onStartUnlimited: () => void
  onJoinPPlus: () => void
}

type Props = {
  isArchive: boolean
  captureStats?: boolean
  mode: GameMode
  header?: JSX.Element
  list: WordList,
  solution: Solution,
  user: User,
  settings: Settings,
  actions: Actions,
  setIsStatsModalOpen: (isOpen: boolean) => void
  onGameStatusChange: (status: GameStatus) => void
  onUpdateStats: (stats: GameStats) => void
  onHint: () => void
  setResponsive: (isResponsive: boolean) => void
}

export type GameStatus = {
  solution: Solution
  guesses: string[]
  isGameWon: boolean
  isGameLost: boolean
}

export const Game = forwardRef(({
  list,
  solution,
  mode,
  user,
  settings,
  actions,
  setIsStatsModalOpen,
  onGameStatusChange,
  onUpdateStats,
  onHint,
  setResponsive,
  header,
  isArchive,
  captureStats=true,
}: Props, ref) => {
  // START STATES
  const {
    showError: showErrorAlert,
    showSuccess: showSuccessAlert,
  } = useAlert()

  const revealTime = getRevealTime(settings.revealSpeed)
  const {achievements, setAchievements, setIsAchievementsModalOpen, grantKeyedAchievement} = useAchievements() 
  const [currentGuess, setCurrentGuess] = useState('')
  const [isRevealing, setIsRevealing] = useState(false)
  const [showPostGameInfo, setShowPostGameInfo] = useState(false)
  const [gaveUp, setGaveUp] = useState(false)
  const [hint, setHint] = useState("")

  const [currentRowClass, setCurrentRowClass] = useState('')
  const [cursorIndex, setCursorIndex] = useState(0)
  const [shiftHotkeyEnabled, setShiftHotkeyEnabled] = useState(false)

  const [newSolve, setNewSolve] = useState(false)
  const growDivRef = useRef<HTMLDivElement>(null);
  const [growDivHeight, setGrowDivHeight] = useState(0)
  const postGameInfoRef = useRef<HTMLDivElement>(null);
  const [postGameInfoHeight, setPostGameInfoHeight] = useState(0)
  const isSm = useMediaQuery({ query: '(min-width: 640px)' })
  const isTall = useMediaQuery({ query: '(min-height: 700px)' })

  const currentGameState = useMemo(() => {
    const loaded = getStoredGameState(mode)
    if (loaded?.solution === solution.word) {
      return loaded 
    }
    return null
  }, [mode, solution.word])

  const guessesFromGameState = useMemo(() => {
    if(isArchive) {
      return []
    }
    return currentGameState ? currentGameState.guesses : []
  }, [isArchive, currentGameState])

  useEffect(() => {
    const handler = () => {
      setGrowDivHeight(0) // reset div height on orientation changes
    }

    const portrait = window.matchMedia("(orientation: portrait)");
 
    portrait.addEventListener("change", handler);
    return () => {
       portrait.removeEventListener("change", handler); // Or window resize
    };
  }, [])

  useEffect(() => {
    setGuesses(guessesFromGameState)
    const storedGaveUp = currentGameState?.gaveUp ?? false
    setGaveUp(storedGaveUp)

    const gameWasWon = guessesFromGameState.includes(solution.word)
    if (storedGaveUp || (guessesFromGameState.length === MAX_CHALLENGES && !gameWasWon)) {
      showErrorAlert(CORRECT_WORD_MESSAGE(solution.word), {
        data: { type: ALERT_DATA_GAME_END },
        persist: true,
      })
    }
  }, [guessesFromGameState, mode, showErrorAlert, solution.word, currentGameState])

  // get current game
  const [guesses, setGuesses] = useState<string[]>(guessesFromGameState)

  const isGameWon = useMemo(() => {
    return guesses[guesses.length-1] === solution.word
  }, [guesses, solution])

  const isGameLost = useMemo(() => {
    return gaveUp || (!isGameWon && guesses.length === MAX_CHALLENGES)
  }, [gaveUp, guesses, isGameWon])

  const isGameOver = useMemo(() => {
    return isGameWon || isGameLost
  }, [isGameWon, isGameLost])

  const gameStatus = useMemo(() => {
    return {solution, guesses, isGameWon, isGameLost}
  }, [solution, guesses, isGameWon, isGameLost])

  const onUpdateStatsWrapper = useCallback((updatedStats: GameStats) => {
    if(guesses.includes(hint)) {
      updatedStats = grantAchievement(updatedStats, "help")
    }
    if(guesses.some((g) => !isOrdinary(g))) {
      updatedStats = grantAchievement(updatedStats, "linguist")
    }
    const oldAchievements = getAchievements(user.stats())
    const newAchievements = getAchievements(updatedStats)
    const diff = [...newAchievements].filter((a) => !oldAchievements.has(a))
    if(diff.length > 0) {
      setAchievements(diff)
    }
    setStatsLocalAndRemote(user, updatedStats)
    onUpdateStats(updatedStats)
  }, [guesses, hint, onUpdateStats, user, setAchievements])

  // END STATES
  
  useImperativeHandle(ref, () => ({
    setHint(w: string) {
      setCurrentGuess(w.toUpperCase())
      setHint(w.toUpperCase())
    },
    giveUp() {
      setGaveUp(true)
      onFailedSolve(MAX_CHALLENGES + 1) // set # of guesses to 7 regardless of true number of guesses
    },
    handleAchievementsModalClose() {
      if(isGameOver) {
        displayPostGameInfo()
      }
    }
  }));

  useLayoutEffect(() => {
    setTimeout(() => {
      if(growDivRef.current && growDivRef.current.clientHeight > 0) {
        setGrowDivHeight(growDivRef.current.clientHeight)
      }
    }, 1)
  }, [growDivRef])


  const setCursorIndexClamped = useCallback(
    (index: number) => {
      setCursorIndex(Math.min(Math.max(index, 0), solution.word.length - 1))
    },
    [solution.word]
  )

  const guessGA = (accepted: boolean) => {
    ReactGA.event({
      category: GA_CATEGORY_GAME,
      action: GA_ACTION_GUESS,
      value: +accepted,
    } as any)
  }

  const onSelectCell = (index: number) => {
    setCursorIndexClamped(index)
  }

  const onArrow = (key: string) => {
    switch (key) {
      case ARROW_RIGHT:
        if (cursorIndex < solution.word.length - 1) {
          setCursorIndexClamped(cursorIndex + 1)
        }
        break
      case ARROW_LEFT:
        if (cursorIndex > 0) {
          setCursorIndexClamped(cursorIndex - 1)
        }
        break

      default:
        return
    }
  }

  const matchingIndicies = (index: number) => {
    return [index].concat(solution.puzzle
      .map((s, i) => i !== index && s === solution.puzzle[index] ? i : -1).filter((i) => i > -1))
  }

  const multiCursorEnabled = () => {
    return settings.cursorType === CURSOR_TYPE_MULTI || shiftHotkeyEnabled
  }

  const cursorIndicies = () => {
    return multiCursorEnabled() ? matchingIndicies(cursorIndex) : [cursorIndex]
  }

  const guessArray = (guess=currentGuess) => {
    const chars = new GraphemeSplitter().splitGraphemes(guess)
    return Array.from({length: solution.word.length})
      .map((v, i) => chars[i] ?? ' ');
  }


  const setCurrentGuessChar = (c: string, index: number, allMatching: boolean) => {
    const chars = guessArray()
    const indicies = allMatching ? matchingIndicies(index): [index] 
    indicies.forEach((i) => {
      chars[i] = c
    });
    setCurrentGuess(chars.join(''))
    setCursorIndexClamped(index + 1)
  }

  const clearCurrentRowClass = () => {
    setCurrentRowClass('')
  }

  const onChar = (value: string) => {
    // Prevent menus from stealing focus when typing.
    (document.activeElement as HTMLElement).blur();
    if (
      cursorIndex < solution.word.length &&
      guesses.length < MAX_CHALLENGES &&
      !isGameWon
    ) {
      setCurrentGuessChar(value, cursorIndex, multiCursorEnabled())
    }
  }

  const onDelete = () => {
    if (cursorIndex >= 0) {
      const { guess, index } = cursorDelete(currentGuess, cursorIndex)
      if(multiCursorEnabled()) {
        const chars = guessArray(guess)
        Array.from(cursorIndicies()).forEach((i) => {
          chars[i] = ' '
        });
        setCurrentGuess(chars.join(''));
      } else {
        setCurrentGuess(guess)
      }
      setCursorIndexClamped(index)
    }
  }

  const onShift = (enabled: boolean) => {
    if(enabled !== shiftHotkeyEnabled) {
      setShiftHotkeyEnabled(enabled)
    }
  }

  const gameFinishGA = (won: boolean, numGuess: number) => {
    ReactGA.event({
      category: GA_CATEGORY_GAME,
      action: GA_ACTION_GAME_FINISH,
      value: +won,
    } as any)
    ReactGA.event({
      category: GA_CATEGORY_GAME,
      action: `game_finish_${solution.index}`,
      value: numGuess,
    } as any)
  }

  const createSolve = (count: number) => {
    return {
      guesses: count,
      mode,
      solution: solution.word.toLowerCase(),
      expert: settings.isExpertMode,
      hard: settings.isHardMode,
    }
  }

  const onFailedSolve = (count: number) => {
    gameFinishGA(false, -1)
    onUpdateStatsWrapper(addStatsForCompletedGame(user, createSolve(count), captureStats))
    showErrorAlert(CORRECT_WORD_MESSAGE(solution.word), {
      data: { type: ALERT_DATA_GAME_END },
      persist: true,
      delayMs: revealTime * solution.word.length + 1,
    })
  }

  const onEnter = () => {
    if (isGameOver) {
      return
    }

    if (
      currentGuess.includes(' ') ||
      !(unicodeLength(currentGuess) === solution.word.length)
    ) {
      guessGA(false)
      setCurrentRowClass('jiggle')
      return showErrorAlert(NOT_ENOUGH_LETTERS_MESSAGE, {
        data: { type: ALERT_DATA_GUESS },
        onClose: clearCurrentRowClass,
      })
    }

    if (!list.isWordInWordList(currentGuess) &&
      currentGuess !== solution.word) {
      guessGA(false)
      setCurrentRowClass('jiggle')
      return showErrorAlert(WORD_NOT_FOUND_MESSAGE, {
        data: { type: ALERT_DATA_GUESS },
        onClose: clearCurrentRowClass,
      })
    }

    // enforce hard mode - all guesses must contain all previously revealed letters
    if (settings.isHardMode) {
      const firstMissingReveal = findFirstUnusedReveal(
        solution.word,
        currentGuess,
        guesses
      )
      if (firstMissingReveal) {
        guessGA(false)
        setCurrentRowClass('jiggle')
        return showErrorAlert(firstMissingReveal, {
          data: { type: ALERT_DATA_GUESS },
          onClose: clearCurrentRowClass,
        })
      }
    }

    // enforce expert mode - all guesses must follow the pattern of the solution
    if (settings.isExpertMode && !isSamePattern(currentGuess, solution.word)) {
      guessGA(false)
      return showErrorAlert(PATTERN_MUST_MATCH_MESSAGE, {
        data: { type: ALERT_DATA_GUESS },
        onClose: clearCurrentRowClass,
      })
    }

    guessGA(true)
    setIsRevealing(true)
    setNewSolve(true)
    setCursorIndex(-1)
    // turn this back off after all
    // chars have been revealed
    setTimeout(() => {
      setIsRevealing(false)
      setCursorIndexClamped(0)
    }, revealTime * (solution.word.length + 2)) // give extra time before transitioning

    const winningWord = currentGuess === solution.word

    if (
      unicodeLength(currentGuess) === solution.word.length &&
      guesses.length < MAX_CHALLENGES &&
      !isGameWon
    ) {
      setGuesses([...guesses, currentGuess])
      setCurrentGuess('')

      if (winningWord) {
        gameFinishGA(true, guesses.length + 1)
        onUpdateStatsWrapper(addStatsForCompletedGame(user, createSolve(guesses.length + 1), captureStats))
      } else if (guesses.length === MAX_CHALLENGES - 1) {
        onFailedSolve(guesses.length+2)
        return
      }
    }
  }

  useEffect(() => {
    if(isArchive || !captureStats) {
      return
    }
    const state: StoredGameState = {
      gameMode: mode,
      guesses,
      solution: solution.word,
      gaveUp: gaveUp
    }
    if(guesses.length > 0) {
      state.hard = settings.isHardMode
      state.expert = settings.isExpertMode
    }
    setStoredGameState(state)
  }, [guesses, solution.word, mode, isArchive, captureStats, settings.isHardMode, settings.isExpertMode, gaveUp])

  const getWinMessage = (numGuesses: number) => {
    const messages = (() => {
      switch(numGuesses) {
        case 1:
        return [
          'Incredible!',
          'Phenomenal!',
          'Outrageous!',
          'Unbelievable!',
        ]
        case 2:
        return [
          'Exceptional!',
          'Outstanding!',
          'Bravo!',
        ]
        case 5:
        case 6:
        return [
          'Close call! You did it!',
          'Phew! You made it!',
          'That was a nail-biter! Nice job!',
        ]
        case 3:
        case 4:
        default:
        return [
          'Awesome!',
          'Great job!',
          'Well done!',
        ]
      }
    })()
    return messages[Math.floor(Math.random()*messages.length)]
  }

  const displayPostGameInfo = useCallback(() => {
    setResponsive(false)
    setShowPostGameInfo(true)
    setTimeout(() => {
			// keep postGameInfo container parent fixed to the correct size
  		if (!postGameInfoRef.current) return;
  		const resizeObserver = new ResizeObserver(() => {
      	if(postGameInfoRef.current && postGameInfoRef.current.clientHeight > 0) {
      	  setPostGameInfoHeight(postGameInfoRef.current.clientHeight)
      	}
  		});
  		resizeObserver.observe(postGameInfoRef.current);
    }, 1)
  }, [postGameInfoRef, setResponsive])

  useEffect(() => {
    if (isGameWon) {
      const delayMs = newSolve
        ? revealTime * solution.word.length
        : revealTime

      showSuccessAlert(getWinMessage(guesses.length), {
        data: { type: ALERT_DATA_GAME_END },
        delayMs,
        onClose: () => {
          if(achievements.current!.length > 0) {
            setIsAchievementsModalOpen(true)
            return
          }
          displayPostGameInfo()
        },
      })
    }

    if (isGameLost) {
      setTimeout(() => {
        displayPostGameInfo()
      }, (solution.word.length + 1) * revealTime)
    }
  }, [
    achievements,
    captureStats,
    isGameWon,
    isGameLost,
    showSuccessAlert,
    solution.word.length,
    newSolve,
    revealTime,
    guesses,
    setResponsive,
    displayPostGameInfo,
    setIsAchievementsModalOpen,
  ])

  const onShare = () => {
    grantKeyedAchievement(user, "share")
  }

  useEffect(() => {
    onGameStatusChange(gameStatus)
  }, [gameStatus, onGameStatusChange])

  const hasHeader = Boolean(header)

  return (<div className="px-1 sm:pb-8 sm:px-6 lg:px-8 flex flex-col grow">
    <div className="flex w-full flex-row justify-center">
      <div className="sm:h-16">
        {hasHeader && header}
      </div>
    </div>
    <div id="container" className="flex grow w-full flex-row justify-evenly">
      {isSm && <Ad user={user} className="mt-4 lg:mr-16 mr-8" type="desktop" />}
      <div className="w-full sm:w-min flex flex-col">
        <Puzzle isDarkMode={settings.isDarkMode} puzzle={solution.puzzle} isCelebrating={isGameWon} revealTime={revealTime} />
        <div className="z-10">
          <Grid
            solution={solution.word}
            guesses={guesses}
            currentGuess={currentGuess}
            isRevealing={isRevealing}
            currentRowClassName={currentRowClass}
            onSelectCell={onSelectCell}
            cursorIndicies={cursorIndicies()}
            revealTime={revealTime}
            isGameOver={isGameOver}
          />
        </div>
        <Transition
          ref={growDivRef}
          show={!showPostGameInfo}
          style={growDivHeight > 0 ? {height: growDivHeight,
            maxHeight: isSm ? '200px': ''} : {}}
          className="grow"
       		enter=""
        	enterFrom=""
        	enterTo=""
        	leave="transition-all duration-1000"
        	leaveFrom="grow"
        	leaveTo="flex-none !h-0">
        </Transition>
        <div className="mb-4 md:w-[800px] w-full flex flex-col items-center justify-center">
        	<Transition className="w-full" show={!showPostGameInfo}>
            <Transition.Child
              className="relative h-[182px] w-full"
       			  enter=""
        		  enterFrom=""
        		  enterTo=""
        		  leave="transition-all duration-1000"
        		  leaveFrom="h-[182px]"
        		  leaveTo="h-0">
              <Transition.Child className="absolute top-0 left-0 right-0 w-full"
       			    enter=""
        		    enterFrom=""
        		    enterTo=""
        		    leave="transition-all duration-1000"
        		    leaveFrom="translate-y-0 opacity-100"
        		    leaveTo="-translate-y-[300px] opacity-0"> 
        	      <Keyboard
        	      onArrow={onArrow}
        	      onChar={onChar}
        	      onDelete={onDelete}
        	      onEnter={onEnter}
        	      onShift={onShift}
        	      onHint={onHint}
        	      solution={solution.word}
        	      guesses={guesses}
        	      isRevealing={isRevealing}
        	      revealTime={revealTime}
        	      gameMode={mode}
        	      enableHints={settings.enableHints}
                isGameOver={isGameOver}
        	      />
              </Transition.Child>
            </Transition.Child>
        	</Transition>
        	<Transition className="w-full" show={showPostGameInfo}>
            <Transition.Child
              style={postGameInfoHeight > 0 ? {height: postGameInfoHeight} : {}}
              className="relative md:w-[800px] w-full"
       		    enter=""
        	    enterFrom=""
        	    enterTo=""
        	    leave="transition-all duration-1000"
        	    leaveFrom="scale-y-0"
        	    leaveTo="scale-y-100">
              <Transition.Child
                ref={postGameInfoRef}
                className="absolute top-0 left-0 right-0 w-full"
       		      enter="transition-all duration-1000"
        	      enterFrom="translate-y-[1000px]"
        	      enterTo="translate-y-0"
        	      leave=""
        	      leaveFrom=""
        	      leaveTo=""> 
        	      <PostGameInfo
        	        gameStatus={gameStatus}
        	        shareUrl=""
        	        settings={settings}
                  handleShareSuccess={onShare}
        	        handleShareToClipboard={actions.handleShareToClipboard}
        	        handleShareFailure={actions.handleShareFailure}
        	        onNextPuzzle={actions.onNextPuzzle}
        	        onStartUnlimited={actions.onStartUnlimited}
        	        onJoinPPlus={actions.onJoinPPlus}
        	        gameMode={mode}
        	        user={user}
        	        list={list}
        	      /> 
              </Transition.Child>
            </Transition.Child>
          </Transition>
				</div>
        {!isSm && <div className="flex items-center justify-center">
          {isTall && <Ad user={user} className="mb-2" type="mobile100" />}
          {!isTall && <Ad user={user} className="mb-2" type="mobile50" />}
        </div>}
      </div>
      {isSm && <Ad user={user} className="mt-4 lg:ml-16 ml-8" type="desktop" />}
    </div>
  </div>
 )
})
